import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ModalTransition } from '@atlaskit/modal-dialog';
import getTimeStamp from 'getTimeStamp';
import CurrentUserContext from 'contexts/CurrentUserContext';
import AccountPickerContext from 'contexts/AccountPickerContext';
import STORE from 'store';
import Tabs from 'components/Tabs';

import TabLabelEditorModal from './TabLabelEditorModal';

export const useEntityDetailsManageTabsLogic = ({
  app,
  tabs,
}: {
  app: EntityDetails.App;
  tabs: EntityDetails.ClientTab[];
}) => {
  // Local tabs to prevent jank between drop and firestore snapshot update
  const [fixedTabs, setFixedTabs] = useState<EntityDetails.ClientTab[]>([]);
  const [variableTabs, setVariableTabs] = useState<EntityDetails.ClientTab[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const currentUser = useContext(CurrentUserContext);
  const { selectedAccountId } = useContext(AccountPickerContext);

  const updateLocalTabs = useCallback((newTabs: EntityDetails.ClientTab[]) => {
    setFixedTabs([]);
    setVariableTabs(newTabs);
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    updateLocalTabs(tabs);
  }, [isLoading, tabs, updateLocalTabs]);

  const removeTabFromApp = useCallback(
    async (tab: EntityDetails.ClientTab) => {
      const newTabs = tabs.filter((t) => t.key !== tab.key);
      updateLocalTabs(newTabs);
      const newApp: EntityDetails.App = {
        ...app,
        tabs: newTabs.map((t) => ({
          typeId: t.typeId,
          type: t.type,
          label: t.label,
        })),
        updatedBy: currentUser.id,
        updatedOn: getTimeStamp(),
      };
      return STORE.contentDistributions
        .getEntityDetailsApps({
          accountId: selectedAccountId,
        })
        .doc(newApp.id)
        .set(newApp);
    },
    [app, currentUser.id, selectedAccountId, tabs, updateLocalTabs],
  );
  const onTabRemoved = useCallback(
    async (tab: EntityDetails.ClientTab) => {
      setIsLoading(true);
      await removeTabFromApp(tab);
      setIsLoading(false);
    },
    [removeTabFromApp],
  );

  const setTabLabel = useCallback(
    async ({ tab, label }: { tab: EntityDetails.ClientTab; label: string }) => {
      const trimmed = label.trim();
      const nextLabel = trimmed === '' ? undefined : trimmed;
      const newTabs = tabs.map((t) =>
        t.key === tab.key ? { ...t, label: nextLabel } : t,
      );
      setIsLoading(true);
      updateLocalTabs(newTabs);
      const newApp: EntityDetails.App = {
        ...app,
        tabs: newTabs.map((t) => ({
          type: t.type,
          typeId: t.typeId,
          label: t.label,
        })),
        updatedBy: currentUser.id,
        updatedOn: getTimeStamp(),
      };

      try {
        await STORE.contentDistributions
          .getEntityDetailsApps({
            accountId: selectedAccountId,
          })
          .doc(newApp.id)
          .set(newApp);
      } finally {
        setIsLoading(false);
      }
    },
    [app, currentUser.id, selectedAccountId, tabs, updateLocalTabs],
  );

  const onOrderChanged = useCallback(
    (newOrder: EntityDetails.ClientTab[]) => {
      setIsLoading(true);
      updateLocalTabs(newOrder);
      const newApp: EntityDetails.App = {
        ...app,
        tabs: newOrder.map((t) => ({
          type: t.type,
          typeId: t.typeId,
          label: t.label,
        })),
        updatedBy: currentUser.id,
        updatedOn: getTimeStamp(),
      };

      STORE.contentDistributions
        .getEntityDetailsApps({
          accountId: selectedAccountId,
        })
        .doc(newApp.id)
        .set(newApp)
        .then(() => {
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    },
    [app, currentUser.id, selectedAccountId, updateLocalTabs],
  );

  return {
    fixedTabs,
    variableTabs,
    onOrderChanged,
    onTabRemoved,
    setTabLabel,
  };
};

const ManageTabsButton = ({
  app,
  tabs,
}: {
  app: EntityDetails.App;
  tabs: EntityDetails.ClientTab[];
}) => {
  const { fixedTabs, variableTabs, onOrderChanged, onTabRemoved, setTabLabel } =
    useEntityDetailsManageTabsLogic({ app, tabs });
  const [editingTab, setEditingTab] = useState<EntityDetails.ClientTab>();

  return (
    <>
      <Tabs.ManageTabs.InlineDialogButton
        fixedTabs={fixedTabs}
        variableTabs={variableTabs}
        onOrderChanged={onOrderChanged}
        onTabRemoved={onTabRemoved}
        onTabEdit={setEditingTab}
      />
      <ModalTransition>
        {editingTab && (
          <TabLabelEditorModal
            tab={editingTab}
            onSave={(label) => setTabLabel({ tab: editingTab, label })}
            onClose={() => setEditingTab(undefined)}
          />
        )}
      </ModalTransition>
    </>
  );
};
export default ManageTabsButton;
